import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Search, Clear, FileDownload } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faCheckCircle, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

const STATUS_CHIPS = {
  new: { color: 'error', label: 'New' },
  viewed: { color: 'warning', label: 'Viewed' },
  resolved: { color: 'success', label: 'Resolved' }
};

const csvEscape = (value) => {
  const s = value == null ? '' : String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (filename, columns, rows) => {
  const lines = [columns.map((c) => csvEscape(c.label)).join(',')];
  rows.forEach((row) => {
    lines.push(columns.map((c) => csvEscape(c.exportValue ? c.exportValue(row) : c.getValue(row))).join(','));
  });
  const blob = new Blob([`\ufeff${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

/**
 * One section of the Monitor page (e.g. Possible Leaks): searchable, sortable,
 * paginated, exportable table of notifications with quick status actions.
 */
const MonitorPanel = ({ title, color, rows, columns, defaultSort = null, onUpdateStatus, exportFileName, emptyText = 'Nothing to report' }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState(defaultSort?.key || null);
  const [sortDirection, setSortDirection] = useState(defaultSort?.direction || 'asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  useEffect(() => {
    setPage(0);
  }, [rows.length, search, sortBy, sortDirection]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) => columns.some((c) => {
      const v = c.getValue(row);
      return v != null && String(v).toLowerCase().includes(term);
    }));
  }, [rows, columns, search]);

  const sorted = useMemo(() => {
    const column = columns.find((c) => c.key === sortBy);
    if (!column) return filtered;
    const list = [...filtered];
    list.sort((a, b) => {
      const av = column.getValue(a);
      const bv = column.getValue(b);
      // Missing values always sink to the bottom regardless of direction
      if (av == null || av === '') return (bv == null || bv === '') ? 0 : 1;
      if (bv == null || bv === '') return -1;
      const cmp = column.numeric
        ? Number(av) - Number(bv)
        : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [filtered, columns, sortBy, sortDirection]);

  const pageRows = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSort = (column) => {
    if (sortBy === column.key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(column.key);
    setSortDirection(column.numeric ? 'desc' : 'asc');
  };

  const openDevice = (row) => {
    if (row?.device?.id) navigate(`/devices/${row.device.id}`);
  };

  return (
    <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', borderTop: `3px solid ${color}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color, fontSize: '1.1rem' }}>
            {title}
          </Typography>
          <Chip size="small" label={rows.length} sx={{ backgroundColor: `${color}20`, color, fontWeight: 'bold' }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by keyword"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 180 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="clear search" onClick={() => setSearch('')} edge="end">
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileDownload />}
            disabled={sorted.length === 0}
            onClick={() => downloadCsv(exportFileName, columns, sorted)}
          >
            Export
          </Button>
        </Box>
      </Box>

      <TableContainer sx={{ maxHeight: 'calc(100vh - 360px)', minHeight: 200, flex: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.numeric ? 'right' : 'left'}
                  sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}
                  sortDirection={sortBy === column.key ? sortDirection : false}
                >
                  <TableSortLabel
                    active={sortBy === column.key}
                    direction={sortBy === column.key ? sortDirection : (column.numeric ? 'desc' : 'asc')}
                    onClick={() => handleSort(column)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 2} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  {search ? 'No matches for this search' : emptyText}
                </TableCell>
              </TableRow>
            ) : pageRows.map((row) => {
              const chip = STATUS_CHIPS[row.status] || { color: 'default', label: row.status };
              return (
                <TableRow key={row.id} hover sx={{ cursor: 'pointer' }} onClick={() => openDevice(row)}>
                  {columns.map((column) => (
                    <TableCell key={column.key} align={column.numeric ? 'right' : 'left'} sx={column.sx}>
                      {column.render ? column.render(row) : (column.getValue(row) ?? '-')}
                    </TableCell>
                  ))}
                  <TableCell>
                    <Chip size="small" color={chip.color} label={chip.label} />
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                    <Tooltip title="Open device">
                      <IconButton size="small" onClick={() => openDevice(row)}>
                        <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" />
                      </IconButton>
                    </Tooltip>
                    {row.status === 'new' && (
                      <Tooltip title="Mark as Viewed">
                        <IconButton size="small" color="primary" onClick={() => onUpdateStatus(row.id, 'viewed')}>
                          <FontAwesomeIcon icon={faEye} size="xs" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {row.status !== 'resolved' && (
                      <Tooltip title="Mark as Resolved">
                        <IconButton size="small" color="success" onClick={() => onUpdateStatus(row.id, 'resolved')}>
                          <FontAwesomeIcon icon={faCheckCircle} size="xs" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={sorted.length}
        page={page}
        onPageChange={(e, next) => setPage(next)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
        rowsPerPageOptions={[25, 50, 100, 250]}
      />
    </Paper>
  );
};

MonitorPanel.propTypes = {
  title: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    getValue: PropTypes.func.isRequired,
    exportValue: PropTypes.func,
    render: PropTypes.func,
    numeric: PropTypes.bool,
    sx: PropTypes.object
  })).isRequired,
  defaultSort: PropTypes.shape({ key: PropTypes.string, direction: PropTypes.oneOf(['asc', 'desc']) }),
  onUpdateStatus: PropTypes.func.isRequired,
  exportFileName: PropTypes.string.isRequired,
  emptyText: PropTypes.string
};

export default MonitorPanel;
